// src/pages/TextTools/PptxToGif.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { convertPptxToGif, checkPptxGifStatus } from '../../services/chatApi';
import { Download, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const PptxToGif = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const [uploadedFile, setUploadedFile] = useState(null);
    const [generatedGif, setGeneratedGif] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);
    const [progressInfo, setProgressInfo] = useState({ current: 0, total: 0, message: '' });

    // GIF speed parameter
    const [gifSpeed, setGifSpeed] = useState(1.0);

    // Effect to poll task status when taskId is set
    useEffect(() => {
        let interval = null;
        if (taskId && taskStatus === 'processing') {
            interval = setInterval(async () => {
                try {
                    const statusResponse = await checkPptxGifStatus(taskId);
                    setTaskStatus(statusResponse.status);

                    if (statusResponse.status === 'processing') {
                        // Update progress info if available
                        if (statusResponse.current !== undefined && statusResponse.total !== undefined) {
                            setProgressInfo({
                                current: statusResponse.current,
                                total: statusResponse.total,
                                message: statusResponse.message
                            });
                        }
                    } else if (statusResponse.status === 'completed') {
                        setGeneratedGif(`data:image/gif;base64,${statusResponse.gif_data}`);
                        clearInterval(interval);
                        
                        // Update credits if returned
                        if (statusResponse.new_credits !== undefined) {
                            console.log(`Credits updated. New balance: ${statusResponse.new_credits}`);
                            setCredits(statusResponse.new_credits);
                        }
                        
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                        setProgressInfo({ current: 0, total: 0, message: '' });
                    } else if (statusResponse.status === 'failed') {
                        setError(statusResponse.error || t('pptxToGif.errorGeneric'));
                        clearInterval(interval);
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                        setProgressInfo({ current: 0, total: 0, message: '' });
                    }
                } catch (err) {
                    console.error('Error checking task status:', err);
                    clearInterval(interval);
                    setTaskId(null);
                    setError(t('pptxToGif.statusCheckError'));
                    setIsLoading(false);
                    setTaskStatus(null);
                    setProgressInfo({ current: 0, total: 0, message: '' });
                }
            }, 2000); // Check every 2 seconds
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [taskId, taskStatus, t, setCredits]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 50 * 1024 * 1024) {
                setError(t('pptxToGif.fileTooLarge'));
                return;
            }
            if (!file.name.toLowerCase().endsWith('.pptx')) {
                setError(t('pptxToGif.invalidFileType'));
                return;
            }
            setUploadedFile(file);
            setError(null);
        }
    };

    const handleConvert = async () => {
        setError(null);
        setGeneratedGif(null);
        setTaskStatus(null);
        setTaskId(null);
        setProgressInfo({ current: 0, total: 0, message: '' });

        if (!uploadedFile) {
            setError(t('pptxToGif.uploadFileFirst'));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            formData.append('gif_speed', gifSpeed);

            const result = await convertPptxToGif(formData);
            
            if (result.task_id) {
                setTaskId(result.task_id);
                setTaskStatus('processing');
            } else {
                setError(t('pptxToGif.errorGeneric'));
                setIsLoading(false);
                setTaskStatus(null);
            }
        } catch (err) {
            setError(err.message || t('pptxToGif.errorGeneric'));
            setIsLoading(false);
            setTaskStatus(null);
        }
    };

    const handleDownload = () => {
        if (!generatedGif) return;

        const link = document.createElement('a');
        link.href = generatedGif;
        link.download = 'presentation.gif';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleClear = () => {
        setUploadedFile(null);
        setGeneratedGif(null);
        setError(null);
        setTaskId(null);
        setTaskStatus(null);
        setGifSpeed(1.0);
        setProgressInfo({ current: 0, total: 0, message: '' });
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange">
                        {t('pptxToGif.title')}
                    </h3>
                    <p className="mt-2 text-brand-dark">
                        {t('pptxToGif.description')}
                    </p>

                    {/* File Upload Section */}
                    <div className="mt-8">
                        <label className="block text-sm font-medium mb-2">
                            {t('pptxToGif.fileLabel')}
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-600 rounded-md cursor-pointer hover:border-brand-orange transition-colors">
                                <Upload className="mr-2" size={20} />
                                <span>{uploadedFile ? uploadedFile.name : t('pptxToGif.chooseFile')}</span>
                                <input
                                    type="file"
                                    accept=".pptx"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    disabled={isLoading}
                                />
                            </label>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                            {t('pptxToGif.fileFormats')}
                        </p>
                    </div>

                    {/* GIF Speed Slider */}
                    <div className="mt-6">
                        <label className="block text-sm font-medium mb-2">
                            {t('pptxToGif.gifSpeed')}: {gifSpeed} {t('pptxToGif.seconds')}
                        </label>
                        <input
                            type="range"
                            min="0.5"
                            max="5.0"
                            step="0.5"
                            value={gifSpeed}
                            onChange={(e) => setGifSpeed(parseFloat(e.target.value))}
                            className="w-full"
                            disabled={isLoading}
                        />
                        <div className="flex justify-between text-xs text-slate-600 mt-1">
                            <span>{t('pptxToGif.faster')}</span>
                            <span>{t('pptxToGif.slower')}</span>
                        </div>
                    </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                            {error}
                        </div>
                    )}

                    {/* Progress Information */}
                    {isLoading && progressInfo.total > 0 && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-blue-900">
                                    {progressInfo.message}
                                </span>
                                <span className="text-sm text-blue-700">
                                    {progressInfo.current}/{progressInfo.total}
                                </span>
                            </div>
                            <div className="w-full bg-blue-200 rounded-full h-2">
                                <div 
                                    className="bg-brand-orange h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${(progressInfo.current / progressInfo.total) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4 mt-6">
                        <button
                            onClick={handleConvert}
                            className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center"
                            disabled={isLoading || taskStatus === 'processing'}
                        >
                            {isLoading || taskStatus === 'processing' ? (
                                <>
                                    <Loader2 className="animate-spin mr-2" size={20} />
                                    {t('pptxToGif.convertingButton')}
                                </>
                            ) : (
                                <>
                                    <ImageIcon className="mr-2" size={20} />
                                    {t('pptxToGif.convertButton')}
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-6 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                            disabled={isLoading || taskStatus === 'processing'}
                        >
                            {t('pptxToGif.clearButton')}
                        </button>
                    </div>

                    {/* Display Generated GIF */}
                    {generatedGif && (
                        <div className="mt-10">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-2xl font-semibold">
                                    {t('pptxToGif.generatedGif')}
                                </h4>
                            </div>
                            
                            <div className="relative border border-slate-300 rounded-md overflow-hidden bg-white p-4">
                                <img 
                                    src={generatedGif} 
                                    alt="Generated GIF" 
                                    className="w-full h-auto max-w-2xl mx-auto"
                                />
                            </div>

                            {/* Download Button */}
                            <div className="flex gap-4 mt-6">
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('pptxToGif.downloadGIF')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Instructions */}
                    <div className="mt-10 p-4 bg-gray-200 border border-brand-orange rounded-md">
                        <h5 className="font-semibold text-blue-900 mb-2">
                            {t('pptxToGif.instructionsTitle')}
                        </h5>
                        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                            <li>{t('pptxToGif.instruction1')}</li>
                            <li>{t('pptxToGif.instruction2')}</li>
                            <li>{t('pptxToGif.instruction3')}</li>
                            <li>{t('pptxToGif.instruction4')}</li>
                        </ul>
                        <p className="mt-3 text-xs text-blue-700">
                            {t('pptxToGif.note')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PptxToGif;
